import { useEffect, useState } from "react";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import type { MultiFactorResolver } from "firebase/auth";
import {
  clearPendingResolver,
  getPendingResolver,
} from "@/auth/mfa-challenge-store";
import {
  MfaCodeActions,
  MfaCodeFields,
  MfaFactorPicker,
} from "@/auth/mfa-resolver-fields";
import { useMfaResolver } from "@/auth/use-mfa-resolver";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RecaptchaNotice } from "@/routes/auth/-recaptcha-notice";

export const Route = createFileRoute("/auth/mfa-challenge")({
  component: MfaChallengePage,
});

const RECAPTCHA_CONTAINER_ID = "mfa-recaptcha-container";

function MfaChallengePage() {
  // Capture the resolver once on mount. Reading the module singleton on every
  // render breaks under StrictMode's mount/cleanup/remount dance, since the
  // cleanup below clears the singleton before the next render reads it.
  const [resolver] = useState<MultiFactorResolver | null>(() =>
    getPendingResolver(),
  );

  // Clear the singleton when the user navigates away (success or cancel) so a
  // stale session can never be reused.
  useEffect(() => {
    return () => clearPendingResolver();
  }, []);

  if (!resolver) {
    return <Navigate to="/auth/login" replace />;
  }

  return <MfaChallengeCard resolver={resolver} />;
}

function MfaChallengeCard({ resolver }: { resolver: MultiFactorResolver }) {
  const mfa = useMfaResolver(resolver, RECAPTCHA_CONTAINER_ID);

  if (!mfa.hint) {
    return (
      <Card className="sm:max-w-96 w-full">
        <CardHeader>
          <CardTitle>Verify it's you</CardTitle>
          <CardDescription>
            Choose a second factor to complete sign-in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <MfaFactorPicker hints={mfa.hints} onSelect={mfa.selectFactor} />
        </CardContent>
      </Card>
    );
  }

  if (!mfa.isPhone && !mfa.isTotp) {
    return (
      <Card className="sm:max-w-96 w-full text-center">
        <CardHeader>
          <CardTitle>Unsupported second factor</CardTitle>
          <CardDescription>
            This account uses a factor we don't recognize. Contact support.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="sm:max-w-96 w-full text-center">
      <CardHeader>
        <CardTitle>Enter your code</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4">
        <div id={RECAPTCHA_CONTAINER_ID} />
        <MfaCodeFields mfa={mfa} />
        <div className="flex w-full gap-2">
          {/* Only offer Back when there's another factor to switch to. */}
          {mfa.hints.length > 1 && (
            <Button
              variant="outline"
              onClick={mfa.clearHint}
              className="flex-1"
            >
              Back
            </Button>
          )}
          <MfaCodeActions mfa={mfa} />
        </div>
        <RecaptchaNotice />
      </CardContent>
    </Card>
  );
}
